import React, { useState } from 'react';
import { Badge, Button, Chip, Grid, InputLabel, OutlinedInput } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { FilterPanel } from '../../../components/filter-panel';
import { GridPanel, dataGridDefaults } from '../../../components/list-grid-panel';
import { RowAction } from '../../../components/row-action';
import { usePagedList } from '../../../hooks/use-paged-list';
import AmendmentAppService from '../../../application/platform/sge/amendment/amendment-app-service';
import { getAmendmentStatus } from '../../../model/sge/amendment/amendment-enums';
import { formatCurrency } from '../../../utils/format';
import { useAmendmentSearchController } from './context/amendment-search-controller';
import { useAmendmentSearchState } from './context/amendment-search-context';
import { countActiveExtraFilters } from './components/amendment-filter-utils';
import { AmendmentMoreFiltersDrawer } from './components/amendment-more-filters-drawer';

export function AmendmentSearchComponent() {
    const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
    const ctrl = useAmendmentSearchController();
    const { parameters, parlamentarians, politicalParties, budgetUnits, budgetPrograms, budgetActions, searchTrigger } = useAmendmentSearchState();
    const { rows, rowCount, loading } = usePagedList(AmendmentAppService.list, parameters, searchTrigger, 'Não foi possível carregar as emendas');
    const activeExtraFiltersCount = countActiveExtraFilters(parameters);

    function handleMoreFiltersSearch() {
        setMoreFiltersOpen(false);
        ctrl.handleSearch();
    }

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'name', headerName: 'Nome', flex: 1, minWidth: 200 },
        { field: 'parlamentarName', headerName: 'Parlamentar', flex: 1, minWidth: 180, valueFormatter: (params) => params.value || '-' },
        { field: 'politicalPartyAbbreviation', headerName: 'Partido', width: 100, valueFormatter: (params) => params.value || '-' },
        { field: 'year', headerName: 'Exercício', width: 100 },
        {
            field: 'status', headerName: 'Status', width: 130,
            renderCell: (params) => {
                const statusInfo = getAmendmentStatus(params.value);
                return <Chip label={statusInfo?.label || '-'} color={statusInfo?.color || 'default'} size="small" />;
            }
        },
        { field: 'totalAmount', headerName: 'Valor Total', width: 150, type: 'number', valueFormatter: (params) => formatCurrency(params.value) },
        {
            field: 'actions', headerName: 'Ações', width: 130, sortable: false,
            renderCell: (params) => (
                <React.Fragment>
                    <RowAction title="Ver detalhes" onClick={() => ctrl.navigateToDetail(params.row.id)}>
                        <VisibilityIcon fontSize="small" />
                    </RowAction>
                    <RowAction title="Editar" onClick={() => ctrl.navigateToEdit(params.row.id)}>
                        <EditIcon fontSize="small" />
                    </RowAction>
                    <RowAction title="Excluir" color="error" onClick={() => ctrl.handleDelete(params.row)}>
                        <DeleteIcon fontSize="small" />
                    </RowAction>
                </React.Fragment>
            )
        }
    ];

    const moreFiltersButton = (
        <Badge badgeContent={activeExtraFiltersCount} color="primary" invisible={activeExtraFiltersCount === 0}>
            <Button variant="outlined" startIcon={<FilterListIcon />} onClick={() => setMoreFiltersOpen(true)}>Mais filtros</Button>
        </Badge>
    );

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader title="Emendas" subtitle="Gerencie as emendas parlamentares" breadcrumbs={[{ label: 'Emendas' }]} />
            </Grid>
            <Grid item xs={12}>
                <FilterPanel onClear={ctrl.handleClearFilters} onSearch={ctrl.handleSearch} extraActions={moreFiltersButton}>
                    <Grid item xs={12}>
                        <InputLabel>Buscar por</InputLabel>
                        <OutlinedInput fullWidth name="input-search" value={parameters.search || ''} onChange={ctrl.handleOnChange} onKeyDown={(e) => e.key === 'Enter' && ctrl.handleSearch()} />
                    </Grid>
                </FilterPanel>
            </Grid>
            <Grid item xs={12}>
                <GridPanel addLabel="Nova emenda" onAdd={ctrl.navigateToCreate}>
                    <DataGrid
                        {...dataGridDefaults}
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        rowCount={rowCount}
                        paginationMode="server"
                        paginationModel={{ page: parameters.page ?? 0, pageSize: parameters.pageSize ?? 25 }}
                        onPaginationModelChange={(model) => ctrl.handlePaginationChange(model.page, model.pageSize)}
                    />
                </GridPanel>
            </Grid>

            <AmendmentMoreFiltersDrawer
                open={moreFiltersOpen}
                parameters={parameters}
                parlamentarians={parlamentarians}
                politicalParties={politicalParties}
                budgetUnits={budgetUnits}
                budgetPrograms={budgetPrograms}
                budgetActions={budgetActions}
                onChange={ctrl.handleOnChange}
                onClear={ctrl.handleClearFilters}
                onSearch={handleMoreFiltersSearch}
                onClose={() => setMoreFiltersOpen(false)}
            />
        </React.Fragment>
    );
}
